import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import StatusBadge from "../../components/common/StatusBadge";
import DashboardFilterBar from "./components/DashboardFilterBar";
import { fetchPartnersInfoPage } from "./dashboardData";

const EMPTY_FILTERS = {
  year: undefined,
  month: undefined,
  project: undefined,
  fleetType: undefined,
  city: undefined,
  zone: undefined,
  partners: [],
};

const COLUMNS = [
  {
    title: "Partner ID",
    dataIndex: "partnerId",
    key: "partnerId",
    sorter: (a, b) => String(a.partnerId).localeCompare(String(b.partnerId)),
    className: "dash-mono",
  },
  {
    title: "Partner Name",
    dataIndex: "partnerName",
    key: "partnerName",
    sorter: (a, b) =>
      String(a.partnerName).localeCompare(String(b.partnerName)),
  },
  {
    title: "Fleet Type",
    dataIndex: "fleetType",
    key: "fleetType",
  },
  {
    title: "Zone",
    dataIndex: "zone",
    key: "zone",
    sorter: (a, b) => String(a.zone).localeCompare(String(b.zone)),
  },
  {
    title: "Nationality",
    dataIndex: "nationality",
    key: "nationality",
  },
  {
    title: "Date Joined",
    dataIndex: "dateOfJoining",
    key: "dateOfJoining",
    render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "—"),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => <StatusBadge status={status} />,
  },
  {
    title: "Days Worked",
    dataIndex: "daysWorked",
    key: "daysWorked",
  },
];

export default function PartnersInfoTab() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total_count: 0,
  });

  const load = useCallback(async (activeFilters, page = 1) => {
    setLoading(true);
    try {
      const result = await fetchPartnersInfoPage(
        activeFilters,
        page,
        pagination.per_page
      );
      setRows(result.records);
      setPagination((prev) => ({
        ...prev,
        page: result.pagination.page || page,
        total_count: result.pagination.total_count || 0,
      }));
    } catch {
      message.error("Failed to load partners info");
    } finally {
      setLoading(false);
    }
  }, [pagination.per_page]);

  useEffect(() => {
    load(filters, 1);
  }, [
    filters.project,
    filters.zone,
    filters.fleetType,
    filters.city,
    filters.partners,
    load,
  ]);

  const handleRefresh = () => {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    load(reset, 1);
  };

  return (
    <div className="dash-tab">
      <DashboardFilterBar
        values={filters}
        onChange={setFilters}
        onRefresh={handleRefresh}
        showYearMonth
        showPartners
        yearMonthFirst
      />

      <div className="dash-body">
        <div className="dash-table-card">
          <DataTable
            columns={COLUMNS}
            dataSource={rows}
            loading={loading}
            rowKey="key"
            pagination={{
              current: pagination.page,
              pageSize: pagination.per_page,
              total: pagination.total_count,
            }}
            onPageChange={(page) => load(filters, page)}
          />
        </div>
      </div>
    </div>
  );
}
